import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  RowSelectionState,
  ColumnFiltersState
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Typography
} from '@mui/material';
import { useEmployeeSelectionBenefitSetColumns } from './columns';
import { useListEmployees } from '../../../../queries/employee';
import { useStore } from '../../../../hooks/useStore';
import { useState, useEffect, useMemo } from 'react';
import { globalFilterFn } from '../../../util/filter';
import { useParams } from 'react-router';
import {
  CreateEmployeeExportRequest,
  CreatePayrollExportRequest,
  CreateReceiptExportRequest
} from '@pxtra-client-lib-legacy/gen';
import { TableToolBar } from '../shared/toolbar';
import Pagination from '../shared/pagination';
import { DEFAULT_PAGE_SIZE, scrollableTableContainerSx } from '../shared/utils';
import { exportFlows } from '../../../../pages/exports/config';
import { observer } from 'mobx-react-lite';
import { DataTableLoadingBar } from '../shared/loading-bar';
import EmptyTable from '../../../icons/svg/empty-table.svg';
import { Trans, useTranslation } from 'react-i18next';
import { SortableTableHeader } from '../shared/sortable-header';
import { ArrowForwardIconSvg, InfoIcon } from '../../../icons';
import { useStepper } from '../../../../pages/actions/partials/actionStepperPartial';
import { useActions } from '../../../../hooks/useActions';
import { Drawer } from '../../atomic/dataDisplay/drawer';

export const EmployeeSelectionBenefitSetTable = observer(() => {
  const { companyStore, exportFlowStore } = useStore();
  const params = useParams();
  const { t } = useTranslation();

  const { data, isFetching } = useListEmployees(companyStore.company?.id, {
    enabled: !!companyStore.company?.id
  });

  const employeesData = useMemo(() => data?.pages?.flatMap((page) => page) ?? [], [data]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(() => {
    const selectedIds =
      (
        exportFlowStore.data as
          CreateEmployeeExportRequest | CreateReceiptExportRequest | CreatePayrollExportRequest
      )?.employees_query?.ids || [];
    return selectedIds.reduce((acc, id) => ({ ...acc, [id]: true }), {});
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const columns = useEmployeeSelectionBenefitSetColumns();

  const { setNextButtonConfig, activeStep, setActiveStep, showInfoDrawer, setShowInfoDrawer } =
    useStepper();
  const { selectedEmployees, setSelectedEmployees } = useActions();
  useEffect(() => {
    setNextButtonConfig({
      text: t('common:NEXT'),
      endIcon: <ArrowForwardIconSvg />,
      disabled: Object.keys(selectedEmployees).length === 0,
      onClick: () => {
        setActiveStep(activeStep + 1);
      }
    });
  }, [selectedEmployees, activeStep]);

  useEffect(() => {
    if (params.type && !exportFlowStore.getIsInitialized(params.type as keyof typeof exportFlows)) {
      exportFlowStore.initializeFlow(params.type as keyof typeof exportFlows);
    }
  }, [params.type]);

  useEffect(() => {
    setRowSelection(selectedEmployees);
  }, []);

  const onRowSelectionChange = (
    updaterOrValue: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)
  ) => {
    const updatedSelection =
      typeof updaterOrValue === 'function' ? updaterOrValue(rowSelection) : updaterOrValue;

    const selectedIds = Object.keys(updatedSelection);

    setRowSelection(updatedSelection);
    setSelectedEmployees(updatedSelection);

    exportFlowStore.updateData({ employees_query: { ids: selectedIds } });
  };

  const table = useReactTable({
    data: employeesData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    globalFilterFn: globalFilterFn,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange,
    getRowId: (row) => row.id ?? 'MISSING_ROW_ID',
    onColumnFiltersChange: setColumnFilters,
    state: {
      pagination,
      globalFilter,
      rowSelection,
      columnFilters
    },
    initialState: {
      columnVisibility: {
        location: false,
        department: false,
        cost_center: false,
        active_benefit: false,
        budget_exhausted: false,
        entry_date: false
      }
    }
  });

  return (
    <>
      <Paper className="overflow-hidden flex flex-col h-[calc(60vh-20px)]">
        <TableToolBar table={table} />

        <TableContainer sx={scrollableTableContainerSx()}>
          <Table stickyHeader className="bg-white" size="small">
            <TableHead sx={{ position: 'sticky', top: 0, zIndex: 2 }}>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <SortableTableHeader
                      key={header.id}
                      header={header}
                      isLoading={isFetching}
                      hasData={employeesData.length > 0}
                    />
                  ))}
                </TableRow>
              ))}
              <TableRow>
                <TableCell
                  sx={{
                    padding: 0,
                    borderBottom: 'none',
                    backgroundColor: 'transparent'
                  }}
                  colSpan={columns.length}
                >
                  <DataTableLoadingBar isLoading={isFetching} />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="!border-none">
                    <div className="flex flex-col items-center justify-center">
                      <img
                        src={EmptyTable}
                        alt={t('export:EMPTY_EMPLOYEE_SELECTION_TABLE')}
                        className="w-64 h-64"
                      />
                      <p className={'mt-4 text-grey-500'}>
                        {t('export:EMPTY_EMPLOYEE_SELECTION_TABLE')}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell className="py-2 border-grey-100" key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Pagination table={table} />
      </Paper>
      <Drawer
        label={t('benefitSet:importantNotes')}
        open={showInfoDrawer}
        closeDrawer={() => setShowInfoDrawer(false)}
      >
        <>
          <Typography variant={'body2'} fontWeight={'bolder'} sx={{ marginBottom: '0.3rem' }}>
            {t('benefitSet:assignmentInfoDrawer:headline1')}
          </Typography>
          <Typography paragraph variant={'body2'}>
            <Trans
              i18nKey={'benefitSet:assignmentInfoDrawer:text1'}
              components={[
                <a
                  key="link"
                  href="https://www.gesetze-im-internet.de/agg/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="open link"
                />
              ]}
            />
          </Typography>
          <Typography paragraph variant={'body2'}>
            <Trans
              i18nKey={'benefitSet:assignmentInfoDrawer:text2'}
              components={{ bold: <strong /> }}
            />
          </Typography>
          <Typography variant={'body2'} fontWeight={'bolder'} sx={{ marginBottom: '0.3rem' }}>
            {t('benefitSet:assignmentInfoDrawer:headline2')}
          </Typography>
          <Typography paragraph variant={'body2'}>
            <Trans
              i18nKey={'benefitSet:assignmentInfoDrawer:text3'}
              components={[
                <a
                  key="link"
                  href="https://support.pxtra.de/platform/kann-ich-meinen-mitarbeitenden-bereits-genutzte-benefits-wieder-entziehen"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="open link"
                />
              ]}
            />
          </Typography>
          <Typography variant={'body2'} fontWeight={'bolder'} sx={{ marginBottom: '0.3rem' }}>
            {t('benefitSet:assignmentInfoDrawer:headline3')}
          </Typography>
          <Typography paragraph variant={'body2'}>
            <Trans
              i18nKey={'benefitSet:assignmentInfoDrawer:text4'}
              components={{ bold: <strong /> }}
            />
          </Typography>

          <Typography
            paragraph
            style={{
              fontSize: 'small',
              color: 'grey.600',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px'
            }}
          >
            <div className="flex text-xs text-grey-600 gap-x-2">
              <InfoIcon className="h-4 w-4" />
              {t('benefitSet:assignmentInfoDrawer:legalNote')}
            </div>
          </Typography>
        </>
      </Drawer>
    </>
  );
});
